import axios from 'axios';
import { queryClient } from '../lib/queryClient';
import { clearUserScopedSession } from '../lib/session';
import { detailToMessage } from '../lib/apiError';
import { askAiConsent } from '../native/aiConsent';
import * as haptics from '../native/haptics';
import i18n from '../i18n';
import { clientPlatform } from '../lib/clientPlatform';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
});

// Codes of an AI provider's refusal (backend utils/ai_errors.py), worded in errors.<code>.
const AI_ERRORS = new Set(['ai_unavailable', 'own_key_no_credit', 'own_key_refused']);

// Add token to requests
// One id per browser tab. The backend tags live refresh hints with the tab
// that caused the change, so this tab can skip refetching what it already
// shows (see NotificationsContext).
export const CLIENT_ID = (typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID()
  : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['X-Client-Id'] = CLIENT_ID;
  config.headers['X-Client-Platform'] = clientPlatform();
  return config;
});

// Endpoints that mutate often but whose responses are noise we don't want
// to use as an invalidation signal (telemetry, polling acks, etc.).
//
// `/access`: /projects/{id}/access - last-accessed-at write fired on every
// project open. Invalidating projects/tasks/documents here turned rapid
// project clicks into a cancel-and-restart storm that left ProjectsPage
// stuck in a skeleton state when in-flight GETs were aborted faster than
// they could complete.
const NON_INVALIDATING_PATHS = [
  '/auth/refresh',
  '/auth/login',
  '/auth/register',
  '/notifications/viewing',  // a screen's heartbeat every 25 s (lib/viewing.js): changes nothing shown
  '/notifications/sse-token',
  '/access',
  // '/unreads/read' also matches '/unreads/read-all' via includes(). Both are
  // already covered by an optimistic cache patch plus the backend's SSE
  // invalidate, so the axios layer would only add a duplicate refetch - and
  // this fires on every scroll through a comment thread. Note this does NOT
  // match '/unreads/unread', which keeps the PATH_TO_QUERY_KEYS rule below.
  '/unreads/read',
  // A schedule's rhythm in words, asked while someone edits it: reads nothing to refetch.
  '/bot-schedules/preview',
  // Database grids update their own caches (optimistic edits, incremental row
  // sync, live hints from the server); a blanket refetch of 50 000 rows after
  // every cell edit would stall them.
  '/databases/',
];

const isMutatingMethod = (method) =>
  ['post', 'put', 'patch', 'delete'].includes((method || '').toLowerCase());

// Map a request URL to the TanStack Query root key that should be
// invalidated. Keep this list small and explicit - the catch-all is to
// invalidate nothing, on the assumption that an endpoint nobody listed
// here doesn't have any cached read partner.
const PATH_TO_QUERY_KEYS = [
  // A task's change shows in Spotlight too (its row there, or a rule that put it in).
  { match: (p) => p.startsWith('/tasks'),         keys: [['tasks'], ['spotlight']] },
  { match: (p) => p.includes('/bot-memory'),      keys: [['bots']] },
  // Opening Spotlight is a POST (it makes the default sections the first time) that only reads:
  // invalidating after it (everything, without a rule) refetched it again, endlessly.
  { match: (p) => p === '/spotlight', keys: [] },
  { match: (p) => p.startsWith('/spotlight'), keys: [['spotlight']] },
  { match: (p) => /\/(bot-learning|bots-paused|bot-budget|bot-skills)/.test(p), keys: [['bots']] },
  { match: (p) => p.startsWith('/bot-profile'),   keys: [['bots']] },
  { match: (p) => /^\/bot-(proposals|rules|skills|library-skills|schedules|secrets)/.test(p) || p.includes('/bot-rules'), keys: [['bots']] },
  { match: (p) => p.startsWith('/agent/'),        keys: [['bots'], ['tasks']] },
  { match: (p) => p.startsWith('/mcp-connections'), keys: [['bots']] },
  { match: (p) => p.startsWith('/tasks') && p.includes('/bot'), keys: [['tasks'], ['bots']] },
  { match: (p) => p.startsWith('/projects') && p.includes('/workspace'), keys: [['bots']] },
  { match: (p) => p.startsWith('/sections'),      keys: [['tasks'], ['projects']] },
  { match: (p) => p.startsWith('/projects'),      keys: [['projects'], ['tasks'], ['documents']] },
  { match: (p) => p.startsWith('/habits'),        keys: [['habits']] },
  { match: (p) => p.startsWith('/labels'),        keys: [['labels'], ['tasks']] },
  { match: (p) => p.startsWith('/task-statuses'), keys: [['custom-statuses'], ['tasks']] },
  { match: (p) => p.startsWith('/task-types'),    keys: [['task-types'], ['tasks']] },
  { match: (p) => p.startsWith('/custom-fields'), keys: [['custom-fields'], ['tasks']] },
  { match: (p) => p.startsWith('/settings'),      keys: [['settings']] },
  { match: (p) => p.startsWith('/notifications'), keys: [['notifications']] },
  { match: (p) => p.startsWith('/documents'),     keys: [['documents']] },
  { match: (p) => p.startsWith('/databases'),     keys: [['databases'], ['documents']] },
  { match: (p) => p.startsWith('/meetings'),      keys: [['meetings']] },
  { match: (p) => p.startsWith('/teams'),         keys: [['teams'], ['tasks']] },
  { match: (p) => p.startsWith('/comments'),      keys: [['tasks'], ['unreads']] },
  { match: (p) => p.startsWith('/unreads'),       keys: [['unreads']] },
  { match: (p) => p.startsWith('/focus-time'),    keys: [['focus-time']] },
  { match: (p) => p.startsWith('/trash'),         keys: [['trash'], ['tasks'], ['projects'], ['documents']] },
  { match: (p) => p.startsWith('/webhooks'),      keys: [['webhooks']] },
  { match: (p) => p.startsWith('/gmail'),         keys: [['gmail'], ['tasks']] },
  { match: (p) => p.startsWith('/admin'),         keys: [['admin']] },
  { match: (p) => p.startsWith('/billing'),       keys: [['billing']] },
  { match: (p) => p.startsWith('/google-calendar'), keys: [['google-calendar'], ['tasks']] },
  { match: (p) => p.startsWith('/mcp-tokens'),    keys: [['mcp-tokens']] },
  // A chat message only changes its chat, which the sender updates itself.
  { match: (p) => p.startsWith('/bot-chats') && p.endsWith('/messages'), keys: [] },
  { match: (p) => /^\/bots\/[^/]+\/files$/.test(p), keys: [] },  // an attachment waiting to be sent
  { match: (p) => p.startsWith('/bot-chats'),     keys: [['bots']] },
  { match: (p) => p.startsWith('/bots'),          keys: [['bots'], ['tasks']] },
  { match: (p) => p.startsWith('/auth/avatar') || p.startsWith('/auth/profile') || p.startsWith('/auth/change-password'),
    keys: [['settings']] },
];

const queryKeysForPath = (path) => {
  const rule = PATH_TO_QUERY_KEYS.find((r) => r.match(path));
  return rule ? rule.keys : null;
};

// Handle auth errors + plan limit errors, and trigger scoped query
// invalidation after every successful mutation so every page that reads
// via TanStack Query stays in sync without per-call refetch wiring.
api.interceptors.response.use(
  (response) => {
    const cfg = response.config || {};
    const path = cfg.url || '';
    const skip = NON_INVALIDATING_PATHS.some((p) => path.includes(p));
    if (isMutatingMethod(cfg.method) && !skip) {
      const keys = queryKeysForPath(path);
      // `cancelRefetch: false` lets in-flight fetches complete instead of
      // tearing them down. Default behavior cancels and restarts, which under
      // rapid mutation traffic (e.g. clicking through 5 projects in a second)
      // turns into an indefinite cancel/restart loop where the active query
      // never resolves and the page is pinned to its loading skeleton.
      if (keys) {
        keys.forEach((key) =>
          queryClient.invalidateQueries({ queryKey: key, cancelRefetch: false })
        );
      } else {
        queryClient.invalidateQueries({ cancelRefetch: false });
      }
    }
    return response;
  },
  (error) => {
    // FastAPI answers request-validation failures with `detail` as an array of
    // {loc, msg, type} objects, while HTTPException sends a string or a
    // structured object. Callers overwhelmingly render `detail` straight into a
    // toast or an error banner, and an array of objects there takes the whole
    // page down with "Objects are not valid as a React child". Collapse only
    // the array form to a sentence - strings pass through, and so do the
    // structured objects some routes rely on (e.g. detail.limit_bytes).
    const detail = error.response?.data?.detail;
    if (Array.isArray(detail)) {
      error.response.data.detail = detailToMessage(detail);
    }
    // An AI provider refused an AI feature (backend utils/ai_errors.py): the person's language, in
    // the places callers show (detail, or the error's message), never the provider's own words.
    if (AI_ERRORS.has(detail?.error)) {
      error.response.data.detail = i18n.t(`errors.${detail.error}`);
      error.message = error.response.data.detail;
    }
    // Only a session that ended (the request carried a token) signs out and goes to the sign-in
    // page. A visitor without one gets 401 from the signed-in endpoints a public page's views
    // also call (the public team page): that page stays, and the app's own pages send signed-out
    // people to sign in through ProtectedRoute.
    if (error.response?.status === 401 && error.config?.headers?.Authorization) {
      localStorage.removeItem('token');
      clearUserScopedSession();
      // The desktop app's own surfaces (/desktop/..., src/desktop/) stay where they are
      // and show their signed-out face; everywhere else the sign-in page takes over.
      if (window.location.pathname.startsWith('/desktop/')) window.location.reload();
      else window.location.href = '/login';
    }
    if (error.response?.status === 428 && error.response.data?.detail === 'ai_consent_required'
        && !error.config?._aiConsentAsked) {
      // Ask for the AI permission once, then repeat the request (src/native/aiConsent.js).
      return askAiConsent().then((granted) => {
        if (granted) return api({ ...error.config, _aiConsentAsked: true });
        error.response.data.detail = i18n.t('ai_consent.declined');
        return Promise.reject(error);
      });
    }
    if (error.response?.status === 402) {
      // BG_110: callers that render their own inline plan-gate message (e.g.
      // AddProjectModal) set `_suppressUpgradePrompt` on the request config so
      // the global upgrade dialog doesn't ALSO fire. Otherwise it opens behind
      // the modal and only surfaces after the user clicks Cancel, looking like
      // an unrelated popup triggered by Cancel.
      if (!error.config?._suppressUpgradePrompt) {
        const detail = error.response.data?.detail;
        window.dispatchEvent(new CustomEvent('upgradNeeded', { detail }));
      }
    }
    return Promise.reject(error);
  }
);

// Tasks API
export const tasksApi = {
  getAll: (params = {}) => api.get('/tasks', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => {
    if (data?.status === 'completed') haptics.done(); // the app: a tap when a task is done
    return api.put(`/tasks/${id}`, data);
  },
  delete: (id) => api.delete(`/tasks/${id}`),
  duplicate: (id) => api.post(`/tasks/${id}/duplicate`),
  // Upcoming occurrences of recurring tasks in [start, end): computed by the server, never stored.
  getOccurrences: (params) => api.get('/tasks/occurrences', { params }),
  bulkUpdate: (data) => api.patch('/tasks/bulk', data),
  // Manual order: siblings in their new order (drag and drop in a list).
  reorder: (taskIds) => api.patch('/tasks/reorder', { task_ids: taskIds }),
  bulkDelete: (data) => api.post('/tasks/bulk-delete', data),

  // Comments
  getComments: (taskId) => api.get(`/tasks/${taskId}/comments`),
  addComment: (taskId, content, mentionedUserIds = [], mentionedBotIds = []) =>
    api.post(`/tasks/${taskId}/comments`, {
      content,
      task_id: taskId,
      mentioned_user_ids: mentionedUserIds,
      mentioned_bot_ids: mentionedBotIds,
    }),
  deleteComment: (commentId) => api.delete(`/comments/${commentId}`),
};

// Projects API
export const projectsApi = {
  getAll: () => api.get('/projects'),
  getById: (id) => api.get(`/projects/${id}`),
  create: (data, config) => api.post('/projects', data, config),
  // BG_91: server-side deep copy (tasks, views, sections, documents) - the
  // old client-side duplicate only re-created the project shell.
  duplicate: (id) => api.post(`/projects/${id}/duplicate`),
  update: (id, data, config) => api.put(`/projects/${id}`, data, config),
  delete: (id) => api.delete(`/projects/${id}`),
  access: (id) => api.post(`/projects/${id}/access`),
  reorder: (items) => api.patch('/projects/reorder', { items }),

  // Views (saved view tabs)
  getViews: (id) => api.get(`/projects/${id}/views`),
  updateViews: (id, views) => api.put(`/projects/${id}/views`, views),
  addView: (id, payload) => api.post(`/projects/${id}/views`, payload),
  updateView: (id, viewId, payload) => api.patch(`/projects/${id}/views/${viewId}`, payload),
  deleteView: (id, viewId) => api.delete(`/projects/${id}/views/${viewId}`),

  // Sharing
  share: (id) => api.post(`/projects/${id}/share`),
  revokeShare: (id) => api.delete(`/projects/${id}/share`),
  sendShareEmail: (id, recipientEmail, shareUrl) => api.post(`/projects/${id}/share-email`, { recipient_email: recipientEmail, share_url: shareUrl }),
  getShared: (token) => axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/projects/shared/${token}`),

  // Sections
  getSections: (projectId) => api.get(`/projects/${projectId}/sections`),
  createSection: (projectId, data) => api.post(`/projects/${projectId}/sections`, { ...data, project_id: projectId }),
  updateSection: (sectionId, data) => api.put(`/sections/${sectionId}`, data),
  deleteSection: (sectionId) => api.delete(`/sections/${sectionId}`),

  // Section Views - per-section view tabs (independent from project-level views)
  getSectionViews: (sectionId) => api.get(`/sections/${sectionId}/views`),
  addSectionView: (sectionId, payload) => api.post(`/sections/${sectionId}/views`, payload),
  updateSectionView: (sectionId, viewId, payload) => api.patch(`/sections/${sectionId}/views/${viewId}`, payload),
  deleteSectionView: (sectionId, viewId) => api.delete(`/sections/${sectionId}/views/${viewId}`),
};

// Labels API
export const labelsApi = {
  getAll: () => api.get('/labels'),
  create: (data) => api.post('/labels', data),
  update: (id, data) => api.put(`/labels/${id}`, data),
  delete: (id) => api.delete(`/labels/${id}`),
};

// Task Types API
export const taskTypesApi = {
  getAll: () => api.get('/task-types'),
  getDefault: () => api.get('/task-types/default'),
  create: (data) => api.post('/task-types', data),
  update: (id, data) => api.put(`/task-types/${id}`, data),
  delete: (id) => api.delete(`/task-types/${id}`),
  reorder: (order) => api.put('/task-types/reorder', order),
};

// Teams API
export const teamsApi = {
  getAll: () => api.get('/teams'),
  getById: (id) => api.get(`/teams/${id}`),
  create: (data) => api.post('/teams', data),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),

  // Invitations
  invite: (teamId, data) => api.post(`/teams/${teamId}/invite`, data),
  getInvites: (teamId) => api.get(`/teams/${teamId}/invites`),
  cancelInvite: (teamId, inviteId) => api.delete(`/teams/${teamId}/invite/${inviteId}`),
  getInviteDetails: (token) => api.get(`/teams/invite/${token}`),
  acceptInvite: (token) => api.post(`/teams/invite/accept/${token}`),
  declineInvite: (token) => api.post(`/teams/invite/decline/${token}`),

  // Members
  removeMember: (teamId, userId) => api.delete(`/teams/${teamId}/members/${userId}`),
  updateMemberRole: (teamId, userId, role) => api.put(`/teams/${teamId}/members/${userId}/role`, { role }),
  getTasks: (teamId) => api.get(`/teams/${teamId}/tasks`),

  // Public sharing (Roadmap / Changelog / Ideas portal)
  share: (teamId) => api.post(`/teams/${teamId}/share`),
  revokeShare: (teamId) => api.delete(`/teams/${teamId}/share`),
  updatePublicSettings: (teamId, data) => api.patch(`/teams/${teamId}/public-settings`, data),
};

// Separate axios instance for the public-team endpoints: forwards the JWT when
// available (so logged-in viewers can upvote / submit ideas) but does NOT
// redirect to /login on 401 - the whole point is that the page renders for
// anonymous visitors.
const publicAxios = axios.create({ baseURL: `${BACKEND_URL}/api` });
publicAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const publicApi = {
  getTeam: (shareToken) => publicAxios.get(`/public/teams/${shareToken}`),
  submitIdea: (shareToken, data) => publicAxios.post(`/public/teams/${shareToken}/ideas`, data),
  upvoteIdea: (shareToken, taskId) => publicAxios.post(`/public/teams/${shareToken}/ideas/${taskId}/upvote`),
  // The iOS / Android apps: the oldest version the API still serves (src/native/NativeBridge.jsx).
  mobileConfig: () => publicAxios.get('/mobile/config'),
};

// The iOS / Android apps: this phone's push token (src/native/push.js).
export const mobileApi = {
  registerDevice: (body) => api.post('/mobile/devices', body),
  // On sign-out the session token is passed in: it is gone from storage a moment later.
  unregisterDevice: (token, authToken) => api.delete('/mobile/devices', {
    data: { token }, ...(authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {}),
  }),
};

// Account: deletion (App Store / Google Play) and the AI permission (App Store 5.1.2(i)).
export const accountApi = {
  deletionInfo: () => api.get('/auth/account/deletion'),
  sendDeletionCode: () => api.post('/auth/account/deletion/code'),
  deleteAccount: (code) => api.post('/auth/account/deletion', { code }),
  setAiConsent: (granted) => api.put('/auth/account/ai-consent', { granted }),
};

// Habits API
export const habitsApi = {
  getAll: () => api.get('/habits'),
  create: (data) => api.post('/habits', data),
  update: (id, data) => api.put(`/habits/${id}`, data),
  delete: (id) => api.delete(`/habits/${id}`),
  complete: (id) => api.post(`/habits/${id}/complete`),
  getScheduledEvents: () => api.get('/habits/scheduled-events'),
};

// Focus Time API
export const focusTimeApi = {
  get: () => api.get('/focus-time'),
  update: (data) => api.put('/focus-time', data),
  // The focus blocks auto-schedule put in the calendar (backend utils/focus_time.py)
  getScheduledEvents: () => api.get('/focus-time/scheduled-events'),
};

// Meetings API
export const meetingsApi = {
  getAll: () => api.get('/meetings'),
  create: (data) => api.post('/meetings', data),
  delete: (id) => api.delete(`/meetings/${id}`),
};

// Calendar API
export const calendarApi = {
  getEvents: (startDate, endDate) => 
    api.get('/calendar/events', { params: { start_date: startDate, end_date: endDate } }),
  createEvent: (data) => api.post('/calendar/events', data),
  deleteEvent: (id) => api.delete(`/calendar/events/${id}`),
};


// Config API (public, no auth)
export const configApi = {
  getPlans: () => api.get('/config/plans'),
};

// Auth API (verification-related)
export const authApi = {
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: () => api.post('/auth/resend-verification'),
};

// Social login (Google / Apple), magic link, password setup, MFA. Mirrors
// backend routes under /api/auth/oauth, /api/auth/magic-link,
// /api/auth/set-password, /api/auth/mfa. Distinct from the existing /api/oauth
// namespace which is the MCP OAuth provider.
export const socialAuthApi = {
  // Sign-in: top-level navigation to backend /start (302 to provider).
  // We don't fetch() this - the caller does window.location.assign().
  startUrl: (provider) => `${BACKEND_URL}/api/auth/oauth/${provider}/start`,
  // After callback redirect, frontend reads ?handoff= and exchanges it.
  consumeHandoff: (handoff) => api.post('/auth/oauth/handoff', { handoff }),
  // Link from Settings (authenticated). Returns { authorize_url }.
  startLink: (provider, body) => api.post(`/auth/oauth/${provider}/link/start`, body),
  // Native (Capacitor) - skip the redirect dance.
  native: (provider, idToken, fullName, authorizationCode) =>
    api.post('/auth/oauth/native', {
      provider, id_token: idToken, full_name: fullName, authorization_code: authorizationCode,
    }),
  listIdentities: () => api.get('/auth/oauth/identities'),
  deleteIdentity: (identityId) => api.delete(`/auth/oauth/identities/${identityId}`),
};

export const magicLinkApi = {
  request: (email) => api.post('/auth/magic-link/request', { email }),
  consume: (token) => api.post('/auth/magic-link/consume', { token }),
};

export const passwordSetupApi = {
  start: () => api.post('/auth/set-password/start'),
  confirm: (otp, newPassword) =>
    api.post('/auth/set-password/confirm', { otp, new_password: newPassword }),
};

export const mfaApi = {
  enrolStart: (password) => api.post('/auth/mfa/totp/enroll/start', { password }),
  enrolConfirm: (code) => api.post('/auth/mfa/totp/enroll/confirm', { code }),
  disable: (password, code) => api.delete('/auth/mfa/totp', { data: { password, code } }),
  regenerateCodes: (password, code) =>
    api.post('/auth/mfa/recovery-codes/regenerate', { password, code }),
  // Used right after primary auth returns mfa_required.
  challenge: (challengeToken, code) =>
    api.post('/auth/mfa/challenge', { challenge_token: challengeToken, code }),
};

// Data export API (GDPR)
export const exportApi = {
  downloadData: () => api.get('/export/data', { responseType: 'blob' }),
  downloadPdf: () => api.get('/export/pdf', { responseType: 'blob' }),
};

// Settings API
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  completeOnboarding: (data) => api.post('/settings/complete-onboarding', data),
  getAnalytics: () => api.get('/settings/analytics/weekly'),
  getTimeBreakdown: () => api.get('/settings/analytics/time-breakdown'),
  clearPlanner: () => api.post('/settings/clear-planner'),
};

// Google Calendar API
export const googleCalendarApi = {
  getAuthUrl: () => api.get('/google-calendar/auth-url'),
  exchangeCode: (code) => api.post('/google-calendar/callback', { code }),
  disconnect: () => api.delete('/google-calendar/disconnect'),
  getStatus: () => api.get('/google-calendar/status'),
  getCalendars: () => api.get('/google-calendar/calendars'),
  updateSettings: (settings) => api.put('/google-calendar/settings', settings),
  sync: () => api.post('/google-calendar/sync'),
  getEvents: (startDate, endDate) =>
    api.get('/google-calendar/events', { params: { start_date: startDate, end_date: endDate } }),
};

// AI API - Enhanced with NLP and auto-scheduling
export const aiApi = {
  // NLP Quick Add - parse natural language and create task
  quickAdd: (text, autoSchedule = true) =>
    api.post('/ai/quick-add', null, { params: { text, auto_schedule: autoSchedule } }),

  // Parse only - get parsed result without creating task
  parse: (text) => api.post('/ai/parse', null, { params: { text } }),

  // Auto-schedule all unscheduled tasks
  autoSchedule: (silent = false) => api.post('/ai/auto-schedule', null, { params: { silent } }),

  // Smart reschedule a task
  reschedule: (taskId, reason = 'conflict') =>
    api.post('/ai/reschedule', null, { params: { task_id: taskId, reason } }),

  // Get suggested time slots
  suggestTimes: (duration = 60, preferredTime = 'any', deadline = null) =>
    api.post('/ai/suggest-times', null, {
      params: { duration, preferred_time: preferredTime, deadline }
    }),

  // Adaptive Learning - analyze completed task history to improve scheduling
  adaptiveLearn: () => api.post('/ai/adaptive-learn'),

  // Monthly AI usage breakdown + remaining quota.
  usage: () => api.get('/ai/usage'),

  // Dictation in browsers without speech recognition: a WAV recording in, { text } out.
  transcribe: (wav, language) => {
    const form = new FormData();
    form.append('audio', wav, 'dictation.wav');
    form.append('language', language || '');
    return api.post('/ai/transcribe', form);
  },
};

// Notifications API
export const notificationsApi = {
  getAll: (params = {}) => api.get('/notifications', { params }),
  getSseToken: () => api.post('/notifications/sse-token'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (data) => api.put('/notifications/settings', data),
  // What this screen shows and whether it is used (lib/viewing.js, backend utils/presence.py).
  // While the page goes away a keepalive fetch still arrives, an axios request may not.
  viewing: (body) => (body.leaving
    ? fetch(`${BACKEND_URL}/api/notifications/viewing`, {
      method: 'POST', keepalive: true, body: JSON.stringify(body),
      headers: { 'Content-Type': 'application/json', 'X-Client-Id': CLIENT_ID,
        Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
    : api.post('/notifications/viewing', body)),
  // The pushes this person's phones may still show (native/push.js removes the rest).
  open: () => api.get('/notifications/open'),
};

// Durable unread-comment indicators on tasks and projects. Separate from the
// notification bell on purpose: the bell is a feed of what happened, these are
// the state of things you have not looked at yet.
export const unreadsApi = {
  get: () => api.get('/unreads'),
  markRead: (commentIds) => api.post('/unreads/read', { comment_ids: commentIds }),
  markUnread: (commentIds) => api.post('/unreads/unread', { comment_ids: commentIds }),
  markTaskRead: (taskId) => api.post('/unreads/read-all', { task_id: taskId }),
};

// Weekly AI digest API
export const weeklyDigestApi = {
  sendTest: () => api.post('/weekly-digest/test'),
  preview: () => api.get('/weekly-digest/preview'),
  latest: () => api.get('/weekly-digest/latest'),
};

// Notion import + sync API
export const notionApi = {
  getAuthUrl: () => api.get('/notion/auth-url'),
  getStatus: () => api.get('/notion/status'),
  disconnect: () => api.post('/notion/disconnect'),
  setSyncEnabled: (enabled) => api.put('/notion/sync-enabled', { enabled }),
  getTree: () => api.get('/notion/tree'),
  startImport: (body) => api.post('/notion/import', body),
  getImport: (id) => api.get(`/notion/imports/${id}`),
};

// ClickUp API — mirrors notionApi shape. Used by ClickUpImportWizard and
// the Integrations card in SettingsPage.
export const clickupApi = {
  getAuthUrl: () => api.get('/clickup/auth-url'),
  getStatus: () => api.get('/clickup/status'),
  disconnect: () => api.post('/clickup/disconnect'),
  setSyncEnabled: (enabled) => api.put('/clickup/sync-enabled', { enabled }),
  getTree: () => api.get('/clickup/tree'),
  startImport: (body) => api.post('/clickup/import', body),
  getImport: (id) => api.get(`/clickup/imports/${id}`),
};

// Webhooks API
export const webhooksApi = {
  getAll: () => api.get('/webhooks'),
  getById: (id) => api.get(`/webhooks/${id}`),
  create: (data) => api.post('/webhooks', data),
  update: (id, data) => api.put(`/webhooks/${id}`, data),
  delete: (id) => api.delete(`/webhooks/${id}`),
  getEvents: (id, limit = 50) => api.get(`/webhooks/${id}/events`, { params: { limit } }),
  test: (id) => api.post(`/webhooks/${id}/test`),
};

// Gmail API
export const gmailApi = {
  getAuthUrl: () => api.get('/gmail/auth-url'),
  exchangeCode: (code) => api.post('/gmail/callback', { code }),
  disconnect: () => api.delete('/gmail/disconnect'),
  getStatus: () => api.get('/gmail/status'),
  // Messages
  getMessages: (params = {}) => api.get('/gmail/messages', { params }),
  getMessage: (id) => api.get(`/gmail/messages/${id}`),
  createTaskFromEmail: (messageId, data = {}) => api.post(`/gmail/messages/${messageId}/to-task`, data),
  // Thread tracking
  getThreads: () => api.get('/gmail/threads'),
  linkEmailToTask: (taskId, threadId) => api.post(`/gmail/tasks/${taskId}/link`, null, { params: { thread_id: threadId } }),
  unlinkEmailFromTask: (taskId) => api.delete(`/gmail/tasks/${taskId}/link`),
  getTaskEmail: (taskId) => api.get(`/gmail/tasks/${taskId}/email`),
  // Auto rules
  getRules: () => api.get('/gmail/rules'),
  createRule: (data) => api.post('/gmail/rules', data),
  updateRule: (id, data) => api.put(`/gmail/rules/${id}`, data),
  deleteRule: (id) => api.delete(`/gmail/rules/${id}`),
  // Sync
  sync: () => api.post('/gmail/sync'),
};

// The desktop app's sign-in through the browser (backend routes/desktop.py, src/desktop/ConnectPage.jsx)
export const desktopApi = {
  handoff: (codeChallenge) => api.post('/desktop/handoff', { code_challenge: codeChallenge, code_challenge_method: 'S256' }),
};

export const searchApi = {
  search: (query) => api.get('/search', { params: { q: query } }),
};

// Task Statuses API
export const taskStatusesApi = {
  getAll: (teamId = null) => {
    const params = teamId ? `?team_id=${teamId}` : '';
    return api.get(`/task-statuses${params}`).then(res => res.data);
  },
  getDefault: (teamId = null) => {
    const params = teamId ? `?team_id=${teamId}` : '';
    return api.get(`/task-statuses/default${params}`).then(res => res.data);
  },
  create: (data, teamId = null) => {
    const params = teamId ? `?team_id=${teamId}` : '';
    return api.post(`/task-statuses${params}`, data).then(res => res.data);
  },
  update: (id, data) => api.put(`/task-statuses/${id}`, data).then(res => res.data),
  delete: (id) => api.delete(`/task-statuses/${id}`).then(res => res.data),
  reorder: (order, teamId = null) => {
    const params = teamId ? `?team_id=${teamId}` : '';
    return api.put(`/task-statuses/reorder${params}`, order).then(res => res.data);
  },
};

// Attachments API
export const attachmentsApi = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/attachments/upload', formData);
  },
  delete: (id, objectKey) =>
    api.delete(`/attachments/${id}`, { params: { object_key: objectKey } }),
};

// Trash API
// Custom Fields API
export const customFieldsApi = {
  getAll: (projectId = null) => {
    const params = projectId ? { project_id: projectId } : {};
    return api.get('/custom-fields', { params });
  },
  create: (data) => api.post('/custom-fields', data),
  update: (id, data) => api.put(`/custom-fields/${id}`, data),
  delete: (id) => api.delete(`/custom-fields/${id}`),
};

export const trashApi = {
  getAll:                       () => api.get('/trash'),
  getCount:                     () => api.get('/trash/count'),
  restoreTask:            (id)  => api.post(`/trash/tasks/${id}/restore`),
  deleteTaskPermanently:  (id)  => api.delete(`/trash/tasks/${id}`),
  restoreProject:         (id)  => api.post(`/trash/projects/${id}/restore`),
  deleteProjectPermanently: (id) => api.delete(`/trash/projects/${id}`),
  restoreDocument:        (id)  => api.post(`/trash/documents/${id}/restore`),
  deleteDocumentPermanently: (id) => api.delete(`/trash/documents/${id}`),
  restoreSection:         (id)  => api.post(`/trash/sections/${id}/restore`),
  deleteSectionPermanently: (id) => api.delete(`/trash/sections/${id}`),
  emptyTrash:                   () => api.delete('/trash'),
};

export const profileApi = {
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/auth/avatar', formData);
  },
  deleteAvatar: () => api.delete('/auth/avatar'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
};

export const activityApi = {
  getProjectActivity: (projectId, params) =>
    api.get(`/projects/${projectId}/activity`, { params }),
};

export const documentsApi = {
  getAll: (params = {}) => api.get('/documents', { params }),
  getById: (id) => api.get(`/documents/${id}`),
  create: (data) => api.post('/documents', data),
  update: (id, data) => api.put(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
  duplicate: (id) => api.post(`/documents/${id}/duplicate`),
  versions: (id) => api.get(`/documents/${id}/versions`),
  version: (id, versionId) => api.get(`/documents/${id}/versions/${versionId}`),
  restoreVersion: (id, versionId) => api.post(`/documents/${id}/versions/${versionId}/restore`),
  exportMarkdown: (id) => api.get(`/documents/${id}/export`, { params: { format: 'md' }, responseType: 'blob' }),
  // Fire-and-forget save that survives page teardown (reload/tab close).
  // axios/XHR requests are aborted when the page unloads, so a debounced
  // save in flight is lost on reload. `keepalive` tells the browser to
  // complete the request even as the document goes away. Used by editors
  // to flush pending edits from a `pagehide`/`visibilitychange` handler.
  // Note: the browser caps total keepalive body size at 64KB.
  updateKeepalive: (id, data) => {
    const token = localStorage.getItem('token');
    return fetch(`${BACKEND_URL}/api/documents/${id}`, {
      method: 'PUT',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
    });
  },
};

// Databases API — schema + rows for type=database Documents.
// Endpoints are scoped to a parent document id.
export const databasesApi = {
  // columns
  listFields:    (docId)            => api.get(`/databases/${docId}/fields`),
  createField:   (docId, data)      => api.post(`/databases/${docId}/fields`, data),
  updateField:   (docId, fid, data) => api.put(`/databases/${docId}/fields/${fid}`, data),
  duplicateField:(docId, fid)       => api.post(`/databases/${docId}/fields/${fid}/duplicate`),
  deleteField:   (docId, fid)       => api.delete(`/databases/${docId}/fields/${fid}`),
  restoreField:  (docId, fid)       => api.post(`/databases/${docId}/fields/${fid}/restore`),
  makePrimary:   (docId, fid)       => api.post(`/databases/${docId}/fields/${fid}/make-primary`),
  reorderFields: (docId, fieldIds)  => api.post(`/databases/${docId}/fields/reorder`, { field_ids: fieldIds }),
  // rows (format=sync: {rows, deleted_ids, synced_at}; `since` = only changes)
  syncRows:      (docId, since)     => api.get(`/databases/${docId}/rows`, { params: { format: 'sync', ...(since ? { since } : {}) } }),
  listRows:      (docId)            => api.get(`/databases/${docId}/rows`),
  createRow:     (docId, data = {}) => api.post(`/databases/${docId}/rows`, data),
  bulkCreateRows:(docId, rows, opts = {}) => api.post(`/databases/${docId}/rows/bulk-create`, { rows, ...opts }),
  updateRow:     (docId, rid, data) => api.put(`/databases/${docId}/rows/${rid}`, data),
  bulkUpdateRows:(docId, updates)   => api.post(`/databases/${docId}/rows/bulk-update`, { updates }),
  deleteRow:     (docId, rid)       => api.delete(`/databases/${docId}/rows/${rid}`),
  bulkDeleteRows:(docId, rowIds)    => api.post(`/databases/${docId}/rows/bulk-delete`, { row_ids: rowIds }),
  restoreRows:   (docId, rowIds)    => api.post(`/databases/${docId}/rows/restore`, { row_ids: rowIds }),
  moveRows:      (docId, rowIds, where) => api.post(`/databases/${docId}/rows/move`, { row_ids: rowIds, ...where }),
  duplicateRows: (docId, rowIds)    => api.post(`/databases/${docId}/rows/duplicate`, { row_ids: rowIds }),
  reorderRows:   (docId, rowIds)    => api.post(`/databases/${docId}/rows/reorder`, { row_ids: rowIds }),
  // views
  listViews:     (docId)            => api.get(`/databases/${docId}/views`),
  createView:    (docId, data)      => api.post(`/databases/${docId}/views`, data),
  updateView:    (docId, vid, data) => api.put(`/databases/${docId}/views/${vid}`, data),
  deleteView:    (docId, vid)       => api.delete(`/databases/${docId}/views/${vid}`),
  reorderViews:  (docId, viewIds)   => api.post(`/databases/${docId}/views/reorder`, { view_ids: viewIds }),
  // derived columns, link and person pickers, CSV
  resolveDerived:(docId, rowIds)    => api.post(`/databases/${docId}/resolve-derived`, rowIds ? { row_ids: rowIds } : {}),
  rowsSummary:   (docId, q = '', limit = 50) => api.get(`/databases/${docId}/rows-summary`, { params: { q, limit } }),
  people:        (docId)            => api.get(`/databases/${docId}/people`),
  importCsv:     (docId, text, header = true) => api.post(`/databases/${docId}/import-csv`, { text, header }),
  exportCsv:     (docId, viewId)    => api.get(`/databases/${docId}/export.csv`, { params: viewId ? { view_id: viewId } : {}, responseType: 'blob' }),
};

// Admin API
export const adminApi = {
  overview: (days) => api.get('/admin/overview', { params: { days } }),
  getUsers: (params = {}) => api.get('/admin/users', { params }),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  audit: (limit = 200) => api.get('/admin/audit', { params: { limit } }),
  insights: (days) => api.get('/admin/insights', { params: { days } }),
  errors: (params = {}) => api.get('/admin/errors', { params }),
  setErrorStatus: (fingerprint, status) => api.put(`/admin/errors/${fingerprint}`, { status }),
  markTest: (userIds, isTest) => api.put('/admin/users/test', { user_ids: userIds, is_test: isTest }),
  closeUsers: (userIds, reason, notify = false) => api.post('/admin/users/close', { user_ids: userIds, reason, notify }),
  setRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
  // data: { plan, seats, expires_at (ISO or null), reason }
  setPlan: (userId, data) => api.put(`/admin/users/${userId}/plan`, data),
  botsPause: () => api.get('/admin/bots/pause'),
  botsUsage: (days) => api.get('/admin/bots/usage', { params: { days } }),
  setBotsPause: (paused) => api.post('/admin/bots/pause', { paused }),
  billingInvoices: (status) => api.get('/admin/billing-invoices', { params: status ? { status } : {} }),
  retryBillingInvoice: (id) => api.post(`/admin/billing-invoices/${id}/retry`),
  contentReports: (status) => api.get('/admin/content-reports', { params: status ? { status } : {} }),
  updateContentReport: (id, status) => api.put(`/admin/content-reports/${id}`, { status }),
};

// Billing / Subscriptions API
export const billingApi = {
  getSubscription: () => api.get('/billing/subscription'),
  createCheckoutSession: (data) => api.post('/billing/checkout-session', data),
  createPortalSession: (data = {}) => api.post('/billing/portal-session', data),
  syncFromStripe: () => api.post('/billing/sync'),
  // The app, right after an in-app purchase (src/native/purchases.js).
  syncStore: () => api.post('/billing/store/sync'),
  invoices: () => api.get('/billing/invoices'),
  invoicePdf: (id) => api.get(`/billing/invoices/${id}/pdf`, { responseType: 'blob' }),
};

// MCP Personal Access Tokens - separate from session JWT, scoped to /mcp.
export const mcpTokensApi = {
  list: () => api.get('/mcp-tokens'),
  create: (data) => api.post('/mcp-tokens', data),
  revoke: (id) => api.delete(`/mcp-tokens/${id}`),
  // Apps connected by signing in (OAuth): Claude, ChatGPT
  connections: () => api.get('/mcp-tokens/connections'),
  disconnect: (clientId) => api.delete(`/mcp-tokens/connections/${encodeURIComponent(clientId)}`),
};

// Spotlight: the person's own short list of tasks (backend routes/spotlight.py).
export const spotlightApi = {
  open: (names) => api.post('/spotlight', { names }),
  marks: () => api.get('/spotlight/marks'),
  addSection: (name) => api.post('/spotlight/sections', { name }),
  renameSection: (id, name) => api.put(`/spotlight/sections/${id}`, { name }),
  deleteSection: (id) => api.delete(`/spotlight/sections/${id}`),
  reorderSections: (ids) => api.post('/spotlight/sections/reorder', { ids }),
  put: (taskId, sectionId, order) => api.put(`/spotlight/items/${taskId}`, { section_id: sectionId, order }),
  remove: (taskId) => api.delete(`/spotlight/items/${taskId}`),
  reorderItems: (sectionId, ids) => api.post(`/spotlight/sections/${sectionId}/order`, { ids }),
  addRule: (rule) => api.post('/spotlight/rules', rule),
  updateRule: (id, rule) => api.put(`/spotlight/rules/${id}`, rule),
  deleteRule: (id) => api.delete(`/spotlight/rules/${id}`),
  settings: (limit) => api.put('/spotlight/settings', { limit }),
};

export const botsApi = {
  models: (params = {}) => api.get('/bots/models', { params }),
  getAISettings: (teamId) => api.get('/bots/ai-settings', { params: teamId ? { team_id: teamId } : {} }),
  myInstructions: () => api.get('/bots/my-instructions'),
  saveMyInstructions: (text) => api.put('/bots/my-instructions', { text }),
  myDefaultModel: () => api.get('/bots/my-default-model'),
  saveMyDefaultModel: (modelId) => api.put('/bots/my-default-model', { default_model: modelId }),
  // Own model keys are the account's: they pay wherever its credits would (no team scope).
  byok: () => api.get('/bots/byok'),
  saveByok: (provider, key) => api.put(`/bots/byok/${provider}`, { key }),
  deleteByok: (provider) => api.delete(`/bots/byok/${provider}`),
  putAISettings: (teamId, data) =>
    api.put('/bots/ai-settings', data, { params: teamId ? { team_id: teamId } : {} }),
  list: (teamId) => api.get('/bots', { params: teamId ? { team_id: teamId } : {} }),
  mine: () => api.get('/bots/mine'),
  openQueen: () => api.post('/bots/queen'),
  activity: () => api.get('/bots/activity'),
  get: (id) => api.get(`/bots/${id}`),
  assignTask: (taskId, botId) => api.put(`/tasks/${taskId}/bot`, { bot_id: botId }),
  create: (data) => api.post('/bots', data),
  update: (id, data) => api.patch(`/bots/${id}`, data),
  move: (id, teamId) => api.post(`/bots/${id}/move`, { team_id: teamId || null }),
  delete: (id) => api.delete(`/bots/${id}`),
  projectBots: (projectId) => api.get(`/projects/${projectId}/bots`),
  addToProject: (projectId, data) => api.post(`/projects/${projectId}/bots`, data),
  removeFromProject: (projectId, botId) => api.delete(`/projects/${projectId}/bots/${botId}`),
  chats: (botId, projectId, q) => api.get(`/bots/${botId}/chats`, {
    params: { ...(projectId ? { project_id: projectId } : {}), ...(q ? { q } : {}) },
  }),
  // Every chat of the person with every bot, in every place (the Chats page): { chats, next, needs_you }.
  allChats: (params = {}) => api.get('/bot-chats', { params }),
  // A project's chats with every bot (the project's chat panel, the sidebar).
  chatCounts: () => api.get('/bot-chats/by-project'),  // { projects: { id: { chats, needs_you } } }
  projectChats: (projectId, q) => api.get(`/projects/${projectId}/bot-chats`, { params: q ? { q } : {} }),
  createChat: (botId, projectId) => api.post(`/bots/${botId}/chats`, { project_id: projectId || null }),
  // A chat in no project into a project (one way); the bot continues there.
  moveChat: (chatId, projectId) => api.post(`/bot-chats/${chatId}/move`, { project_id: projectId }),
  chat: (chatId) => api.get(`/bot-chats/${chatId}`),
  deleteChat: (chatId) => api.delete(`/bot-chats/${chatId}`),
  updateChat: (chatId, data) => api.patch(`/bot-chats/${chatId}`, data),
  retryChat: (chatId) => api.post(`/bot-chats/${chatId}/retry`),
  saveAsDocument: (messageId) => api.post(`/bot-messages/${messageId}/document`),
  cancelWaiting: (messageId) => api.delete(`/bot-messages/${messageId}/steer`),
  rateAnswer: (messageId, value, note) => api.put(`/bot-messages/${messageId}/feedback`, { value, ...(note ? { note } : {}) }),
  reportAnswer: (messageId, note) => api.post(`/bot-messages/${messageId}/report`, { note }),
  sendMessage: (chatId, content, fileIds = [], modelId) => api.post(`/bot-chats/${chatId}/messages`, {
    content, file_ids: fileIds, ...(modelId !== undefined ? { model_id: modelId || '' } : {}),
  }),
  uploadFile: (botId, projectId, file, onProgress) => {
    const form = new FormData();
    if (projectId) form.append('project_id', projectId);  // none: a chat in no project
    form.append('file', file);
    return api.post(`/bots/${botId}/files`, form, {
      onUploadProgress: (e) => onProgress?.(e.total ? e.loaded / e.total : 0),
    });
  },
  fileBlob: (fileId) => api.get(`/bot-files/${fileId}`, { responseType: 'blob' }),
  shotBlob: (shotId) => api.get(`/agent/shots/${shotId}`, { responseType: 'blob' }),
  memory: (projectId) => api.get(`/projects/${projectId}/bot-memory`),
  addMemory: (projectId, data) => api.post(`/projects/${projectId}/bot-memory`, data),
  forgetMemory: (entryId) => api.delete(`/bot-memory/${entryId}`),
  undoMemoryEvent: (eventId) => api.post(`/bot-memory-events/${eventId}/undo`),
  profile: () => api.get('/bot-profile'),
  addProfileFact: (content) => api.post('/bot-profile', { content }),
  cancelRun: (runId) => api.post(`/agent/runs/${runId}/cancel`),
  shareSecret: (approvalId, value, remember) => api.post(`/agent/approvals/${approvalId}/secret`, { value, remember }),
  secrets: (projectId) => api.get(`/projects/${projectId}/bot-secrets`),
  projectPaused: (projectId) => api.get(`/projects/${projectId}/bots-paused`),
  setProjectPaused: (projectId, paused) => api.put(`/projects/${projectId}/bots-paused`, { paused }),
  setProjectBudget: (projectId, credits) => api.put(`/projects/${projectId}/bot-budget`, { credits }),
  deleteSecret: (id) => api.delete(`/bot-secrets/${id}`),
  learning: (projectId) => api.get(`/projects/${projectId}/bot-learning`),
  schedules: (botId, projectId) => api.get(`/bots/${botId}/schedules`, { params: { project_id: projectId } }),
  createSchedule: (botId, data) => api.post(`/bots/${botId}/schedules`, data),
  updateSchedule: (id, data) => api.patch(`/bot-schedules/${id}`, data),
  deleteSchedule: (id) => api.delete(`/bot-schedules/${id}`),
  runSchedule: (id) => api.post(`/bot-schedules/${id}/run-now`),
  allSchedules: () => api.get('/bot-schedules'),
  schedulePreview: (cadence, timezone) => api.post('/bot-schedules/preview', { cadence, timezone }),
  scheduleRuns: (id) => api.get(`/bot-schedules/${id}/runs`),
  acceptProposal: (id, edits = {}) => api.post(`/bot-proposals/${id}/accept`, edits),
  rejectProposal: (id) => api.post(`/bot-proposals/${id}/reject`),
  addRule: (projectId, text) => api.post(`/projects/${projectId}/bot-rules`, { text }),
  setAutoLearn: (projectId, enabled) => api.put(`/projects/${projectId}/bot-learning/auto`, { enabled }),
  deleteRule: (id) => api.delete(`/bot-rules/${id}`),
  updateSkill: (id, data) => api.patch(`/bot-skills/${id}`, data),
  shareSkill: (id) => api.post(`/bot-skills/${id}/share`),
  removeLibrarySkill: (id) => api.delete(`/bot-library-skills/${id}`),
  createSkill: (projectId, data) => api.post(`/projects/${projectId}/bot-skills`, data),
  skillHistory: (id) => api.get(`/bot-skills/${id}/history`),
  restoreSkill: (id, versionId) => api.post(`/bot-skills/${id}/restore/${versionId}`),
  undoProposal: (id) => api.post(`/bot-proposals/${id}/undo`),
  tidySkills: (projectId) => api.post(`/projects/${projectId}/bot-learning/tidy`),
  credits: (teamId) => api.get('/bot-credits', { params: teamId ? { team_id: teamId } : {} }),
  buyCredits: (teamId, pack = '1000', currency = 'usd', lang = 'en') => api.post('/bot-credits/checkout', { pack, team_id: teamId || null, currency, lang }),
  confirmCredits: (sessionId) => api.post('/bot-credits/confirm', { session_id: sessionId }),
  workspace: (projectId) => api.get(`/projects/${projectId}/workspace`),
  startWorkspace: (projectId) => api.post(`/projects/${projectId}/workspace/start`),
  pauseWorkspace: (projectId) => api.post(`/projects/${projectId}/workspace/pause`),
  takeOverWorkspace: (projectId) => api.post(`/projects/${projectId}/workspace/take-over`),
  giveBackWorkspace: (projectId, note) => api.post(`/projects/${projectId}/workspace/give-back`, { note }),
  removeWorkspace: (projectId) => api.delete(`/projects/${projectId}/workspace`),
  workspaceFiles: (projectId, path) => api.get(`/projects/${projectId}/workspace/files`, { params: { path } }),
  workspaceDownload: (projectId, path) =>
    api.get(`/projects/${projectId}/workspace/download`, { params: { path }, responseType: 'blob' }),
  connections: (botId, projectId) => api.get(`/bots/${botId}/connections`, { params: projectId ? { project_id: projectId } : {} }),
  connect: (botId, data) => api.post(`/bots/${botId}/connections`, data),
  importConnections: (botId, data) => api.post(`/bots/${botId}/connections/import`, data),
  finishConnect: (state, code) => api.post('/mcp-connections/oauth/callback', { state, code }),
  refreshConnection: (id) => api.post(`/mcp-connections/${id}/refresh`),
  reconnect: (id, data) => api.post(`/mcp-connections/${id}/reconnect`, data || {}),
  updateConnection: (id, data) => api.patch(`/mcp-connections/${id}`, data),
  disconnect: (id) => api.delete(`/mcp-connections/${id}`),
  decideApproval: (approvalId, approve, note, extra = {}) => api.post(`/agent/approvals/${approvalId}`, { approve, note, ...extra }),
  taskStatus: (taskId) => api.get(`/tasks/${taskId}/bot-status`),
  reviewTask: (taskId, decision, feedback) => api.post(`/tasks/${taskId}/bot/review`, { decision, feedback }),
};

/**
 * Follow a bot run's live events (server-sent events over fetch, so the JWT
 * goes in a header). Calls onEvent(event, id) for each event and resumes after
 * network drops from the last event id. `after` starts after an event already
 * seen (a run that waited for the person and goes on), else from the first.
 * Returns a function that stops listening.
 */
export const streamRunEvents = (runId, onEvent, after) => {
  const controller = new AbortController();
  let lastId = after || null;
  let finished = false;

  const connect = async (attempt) => {
    try {
      const headers = { Accept: 'text/event-stream' };
      const token = localStorage.getItem('token');
      if (token) headers.Authorization = `Bearer ${token}`;
      if (lastId) headers['Last-Event-ID'] = lastId;
      const res = await fetch(`${BACKEND_URL}/api/agent/runs/${runId}/events`, {
        headers, signal: controller.signal,
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep;
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const chunk = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          let data = '';
          chunk.split('\n').forEach((line) => {
            if (line.startsWith('id: ')) lastId = line.slice(4);
            else if (line.startsWith('data: ')) data += line.slice(6);
          });
          if (!data) continue;
          const event = JSON.parse(data);
          if (event.type === 'done') finished = true;
          onEvent(event, lastId);
        }
      }
    } catch {
      if (controller.signal.aborted) return;
    }
    if (!finished && !controller.signal.aborted && attempt < 5) {
      setTimeout(() => connect(attempt + 1), 1000 * (attempt + 1));
    }
  };

  connect(0);
  return () => controller.abort();
};

export default api;
